import clsx from 'clsx'
import React, { useCallback, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Fieldset from '@/components-latest/layout/Fieldset'
import Button from '@/components-latest/primitives/Button'
import { Edit, EyeOff, EyeOpen } from '@/components-latest/primitives/Icons'
import MoreOptions, {
  MoreOptionsItem
} from '@/components-latest/primitives/MoreOptions'
import Text from '@/components-latest/primitives/Text'
import { NumericField } from '@/components-latest/shared/Form'
import CommonModal from '@/components-latest/shared/Modals/CommonModal'
import { IMenuItem } from '@/ducks/menu/menu.types'
import { selectCurrencyCode } from '@/ducks/store/selectors/store.selectors'
import { formatPrice } from '@/utils/currency.utils'

import { IEditMenuItemFormData } from '../EditMenuItemModal'
import Styles from './EditMenuItemAddons.module.scss'

export type IPackType = 'addons' | 'base_pack' | 'type_pack'

type EditItemProps = {
  isVisible: boolean
  id: string
  disabled?: boolean
  name: string
  isError?: boolean
  price: number
  currency: string
  onEditClick: (id: string) => void
  onVisibilityClick: (id: string) => void
}
const EditItem = ({
  isVisible,
  id,
  disabled,
  name,
  isError,
  price,
  currency,
  onEditClick,
  onVisibilityClick
}: EditItemProps) => {
  const { t } = useTranslation()

  return (
    <div key={id} className={Styles.addon}>
      <div
        className={clsx({
          [Styles.faded]: !isVisible
        })}>
        <Text variant="nextSubtitle" weight="extraBold">
          {name}
        </Text>
        <Text
          className={clsx(isError && Styles.error)}
          variant="nextBody"
          weight="regular">
          {formatPrice(price, currency, {
            signDisplay: 'never'
          })}
        </Text>
      </div>
      <MoreOptions data-testid={`edit-menu-item-more-${id}`}>
        <MoreOptionsItem
          disabled={disabled}
          className={clsx(isError && Styles.error)}
          id={id}
          icon={<Edit />}
          label={t('menu.edit-modal.option-edit')}
          onClick={onEditClick}
        />
        <MoreOptionsItem
          disabled={disabled}
          id={id}
          icon={isVisible ? <EyeOff /> : <EyeOpen />}
          label={
            isVisible
              ? t('menu.edit-modal.option-hide')
              : t('menu.edit-modal.option-show')
          }
          onClick={onVisibilityClick}
        />
      </MoreOptions>
    </div>
  )
}

type Props = {
  isLoading?: boolean
  menuItem: IMenuItem | null
  form: UseFormReturn<IEditMenuItemFormData>
}

const EditMenuItemAddons = ({ isLoading, menuItem, form }: Props) => {
  const { t } = useTranslation()
  const currency = useSelector(selectCurrencyCode) || ''

  const {
    formState: { errors },
    control,
    setValue,
    setError,
    watch
  } = form

  const [activeEditItem, setActiveEditItem] = useState<{
    type: IPackType
    id: string
  } | null>(null)

  const closeAddonEditPriceModal = useCallback(() => {
    setActiveEditItem(null)
    setValue('_modalPrice', 0)
  }, [setValue])

  const addonsFormValues = watch('addons')
  const basePackFormValues = watch('base_pack')
  const typePackFormValues = watch('type_pack')

  const currentPriceInModal = watch('_modalPrice')

  const onEditAddonPriceClickCreator =
    (packType: IPackType) => (addonId: string) => {
      if (!addonId || !packType) return

      const dataset = {
        addons: addonsFormValues,
        base_pack: basePackFormValues,
        type_pack: typePackFormValues
      }

      setActiveEditItem({ id: addonId, type: packType })
      setValue(`_modalPrice`, dataset?.[packType]?.[addonId]?.price || 0)
    }

  const onModalPriceSubmit = () => {
    if (!activeEditItem || !activeEditItem.type || !activeEditItem.id) {
      closeAddonEditPriceModal()
      return
    }

    setValue(
      `${activeEditItem.type}.${activeEditItem.id}.price`,
      currentPriceInModal || 0
    )
    closeAddonEditPriceModal()
  }

  const onAddonVisibilityChangeCreator =
    (visibilityValue: 1 | 0, packType: IPackType) => (addonId: string) => {
      setValue(`${packType}.${addonId}.visibility`, visibilityValue)
    }

  const cleanFieldErrorCreator =
    (fieldName: `${IPackType}.${string}.${'visibility' | 'price'}`) => () => {
      setError(fieldName, { message: '' })
    }

  const activeEditPriceItemId = activeEditItem?.id as string
  const activeEditPriceItemType = activeEditItem?.type as IPackType

  return (
    <div className={Styles.addonsList}>
      <CommonModal
        title={t('menu.edit-modal.edit-price-title')}
        className={Styles.priceModal}
        body={
          <>
            <Fieldset
              variant="nextBody"
              weight="semiBold"
              label={t('menu.edit-modal.edit-price-label')}>
              <NumericField
                data-testid={`edit-pack-price-${activeEditPriceItemId}`}
                data-error={
                  !!errors?.[activeEditPriceItemType]?.[activeEditPriceItemId]
                    ?.price?.message
                }
                onKeyPress={e => {
                  if (e.key === 'Enter') {
                    onModalPriceSubmit()
                  }
                }}
                name="_modalPrice"
                intent={
                  errors?.[activeEditPriceItemType]?.[activeEditPriceItemId]
                    ?.price?.message
                    ? 'negative'
                    : 'default'
                }
                control={control}
                onFocus={cleanFieldErrorCreator(
                  `${activeEditPriceItemType}.${activeEditPriceItemId}.price`
                )}
                disabled={isLoading}
              />
            </Fieldset>
            <Button
              disabled={isLoading}
              className={Styles.priceSave}
              variant="primary"
              onClick={onModalPriceSubmit}>
              {t('menu.edit-modal.edit-price-submit')}
            </Button>
          </>
        }
        open={!!activeEditItem}
        onOpenChange={closeAddonEditPriceModal}
      />
      {!!menuItem?.addons?.length && (
        <Text
          className={Styles.category}
          variant="nextTitle-3"
          weight="extraBold"
          asChild>
          <p>{t('menu.edit-modal.addonslist-title')}</p>
        </Text>
      )}
      {menuItem?.addons.map(({ name, _id }) => {
        const currentFormAddonPrice = addonsFormValues?.[_id]?.price
        const isVisible = addonsFormValues?.[_id]?.visibility === 1

        const isError = !!errors?.addons?.[_id]?.price?.message

        return (
          <EditItem
            key={_id}
            isVisible={isVisible}
            id={_id}
            name={name.en}
            price={currentFormAddonPrice}
            currency={currency}
            isError={isError}
            onEditClick={onEditAddonPriceClickCreator('addons')}
            onVisibilityClick={onAddonVisibilityChangeCreator(
              isVisible ? 0 : 1,
              'addons'
            )}
          />
        )
      })}
      {menuItem?.base_pack.map(({ sub_pack, _id, name }) => {
        return (
          <div key={_id}>
            <Text
              className={Styles.category}
              variant="nextTitle-3"
              weight="extraBold"
              asChild>
              <p>{name?.['en']}</p>
            </Text>
            {sub_pack.map(({ name, _id }) => {
              const currentFormAddonPrice = basePackFormValues?.[_id]?.price
              const isVisible = basePackFormValues?.[_id]?.visibility === 1

              const isError = !!errors?.base_pack?.[_id]?.price?.message

              return (
                <EditItem
                  key={_id}
                  isVisible={isVisible}
                  id={_id}
                  name={name.en}
                  price={currentFormAddonPrice}
                  currency={currency}
                  isError={isError}
                  onEditClick={onEditAddonPriceClickCreator('base_pack')}
                  onVisibilityClick={onAddonVisibilityChangeCreator(
                    isVisible ? 0 : 1,
                    'base_pack'
                  )}
                />
              )
            })}
          </div>
        )
      })}
      {menuItem?.type_pack.map(({ t_sub_pack, _id, t_name }) => {
        return (
          <div key={_id}>
            <Text
              className={Styles.category}
              variant="nextTitle-3"
              weight="extraBold"
              asChild>
              <p>{t_name?.['en']}</p>
            </Text>
            {t_sub_pack?.map(({ name, _id }) => {
              const currentFormAddonPrice = typePackFormValues?.[_id]?.price
              const isVisible = typePackFormValues?.[_id]?.visibility === 1

              const isError = !!errors?.type_pack?.[_id]?.price?.message

              return (
                <EditItem
                  key={_id}
                  isVisible={isVisible}
                  id={_id}
                  name={name.en}
                  price={currentFormAddonPrice}
                  currency={currency}
                  isError={isError}
                  onEditClick={onEditAddonPriceClickCreator('type_pack')}
                  onVisibilityClick={onAddonVisibilityChangeCreator(
                    isVisible ? 0 : 1,
                    'type_pack'
                  )}
                />
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

export default EditMenuItemAddons
